import React, { forwardRef, useRef, useState } from "react";
import { Box, Checkbox, Grid, Stack } from "@mui/material";
import html2pdf from "html2pdf.js";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import { PhysicalOnePagerResultStyle } from "./PhysicalOnePagerResultStyle";
import VSNTypography from "../../../shared/VSNTypography";
import VSNBreadcrumbs from "../../../shared/VSNBreadcrumbs";
import { FrontLineLogo, StarIcon } from "../../../shared/icon";
import VSNButton from "../../../shared/VSNButton";
import { ReactComponent as VasanaLogo } from "../../../assets/header/svg/vasana_logo.svg";
import MRFResultContainer from "../../../container/MRF.container";
import VSNImage from "../../../shared/VSNImage";
import VSNLoader from "../../../shared/VSNLoader";
import { PDFHeader } from "./PhysicalDashboardPDFCommonComponent";

const PrintComponent = forwardRef((props, ref) => {
  const classes = PhysicalOnePagerResultStyle();
  const { result, header } = props;

  return (
    <Box sx={{ visibility: "hidden", height: 0, overflow: "hidden" }}>
      <Box ref={ref}>
        <PDFHeader title=" Medical Recommendation Form" header={header} />

        <Grid item xl={6} lg={6} xs={12}>
          <Stack
            className={classes.assessmentReportCard}
            sx={{
              background: "#FAF6F0 !important",
            }}
          >
            <VSNTypography
              variant="h3"
              component="h3"
              className={classes.mdHeadingprimary}
              mb={1}
              sx={{
                fontSize: "16px !important",
                color: "#B29263 !important",
                "&:before": { background: "#B29263 !important" },
              }}
            >
              {result?.mrf_card?.title}
            </VSNTypography>
            <VSNTypography
              component="p"
              className={classes.desc}
              sx={{ fontSize: "14px !important" }}
            >
              {result?.mrf_card?.duty_with_limitation_and_days}
            </VSNTypography>

            <Box py={1} display="flex">
              <Box sx={{ height: 12, width: 12 }} mr={2} mt={0.5}>
                <StarIcon />
              </Box>
              <VSNTypography
                sx={{
                  fontSize: "14px !important",
                  fontWeight: 500,
                  color: "#0A0A0B",
                  lineHeight: "24px",
                }}
              >
                {result?.mrf_card?.medical_condition}
              </VSNTypography>
            </Box>

            <VSNTypography
              component="p"
              className={classes.desc}
              pt={1}
              sx={{ fontSize: "14px !important" }}
            >
              Provider Name : {result?.mrf_card?.provider_name ?? "-"}
            </VSNTypography>
          </Stack>
        </Grid>

        <Stack
          className={classes.assessmentReportCard}
          position="relative"
          overflow="hidden"
          mt="25px"
          pb="92px"
          sx={{
            background: "#FAF6F0 !important",
          }}
        >
          <VSNTypography
            variant="h3"
            component="h3"
            className={classes.mdHeadingprimary}
            mb={1}
            sx={{
              fontSize: "16px !important",
              backgroundColor: "pink !importnant",
              color: "#B29263 !important",
              "&:before": { background: "#B29263 !important" },
            }}
          >
            {result?.performance_evaluation_card?.title}
          </VSNTypography>
          <Stack direction="row" alignItems="center" spacing={4} mt={1.75}>
            <Stack
              direction={{ md: "row" }}
              sx={{
                "&.MuiStack-root": {
                  position: "relative",
                  gap: "0 64px",
                },
              }}
              position="relative"
            >
              <Stack>
                {result?.performance_evaluation_card?.tasks?.map(
                  ({ is_checked, phrase, breack = "avoid" }, index) => {
                    const isLastElement =
                      index ===
                      result?.performance_evaluation_card?.tasks?.length - 1;
                    return (
                      <Stack
                        direction="row"
                        alignItems="start"
                        gap={1}
                        key={index}
                        sx={{
                          borderBottom:
                            index === 4 || isLastElement
                              ? "none"
                              : "1px solid #D5B98B",
                          height: "fit-content",
                          pageBreakAfter: index === 4 ? "always" : "",
                        }}
                        padding={3}
                      >
                        <Checkbox
                          checked={is_checked}
                          sx={{
                            padding: 0,
                            paddingTop: "2.5px",
                            borderRadius: 4,
                            "&.Mui-checked": {
                              color: "#B29263",
                            },
                            "&.MuiCheckbox-root": {
                              color: "#B29263",
                            },
                          }}
                        />
                        <VSNTypography sx={{ fontSize: "14px !important" }}>
                          {phrase}
                        </VSNTypography>
                      </Stack>
                    );
                  }
                )}
              </Stack>
            </Stack>
          </Stack>
          <Box position="absolute" sx={{ bottom: "16px", right: "20px" }}>
            <VSNTypography sx={{ textAlign: "end", paddingBottom: "6px" }}>
              Powered by
            </VSNTypography>
            <VasanaLogo width="140px" height="31" />
          </Box>
        </Stack>
      </Box>
    </Box>
  );
});

export default function MRFDashboardResult({
  endPointToCall,
  showBreadCrumbs = true,
  breadCrumbAndButton,
  releaseButton,
  assessmentUserInfo,
  viewAllResult,
  from,
}) {
  const classes = PhysicalOnePagerResultStyle();
  const {
    AssessmentResult: containerResult,
    loader,
    currentAssessment,
  } = from === "viewAll" ? {} : MRFResultContainer({ endPointToCall });

  const AssessmentResult = from === "viewAll" ? viewAllResult : containerResult;

  const printRef = useRef();

  const [loading, setLoading] = useState(false);

  const breadcrumbsRoutes = [
    {
      path: "/assessments",
      label: "Assessments",
    },
    {
      path: `/assessments/${currentAssessment?.assessment_id}`,
      label: `${currentAssessment?.assessment_name} Dashboard`,
    },
  ];

  const handleDownloadPDF = () => {
    setLoading(true);
    const element = printRef.current;

    const adminFilename = `${assessmentUserInfo?.name}_${assessmentUserInfo?.assessment_name}.pdf`;
    const userFilename = `${AssessmentResult?.info?.name}_${currentAssessment?.assessment_name}.pdf`;
    const fileName = assessmentUserInfo ? adminFilename : userFilename;

    const options = {
      margin: 6,
      filename: fileName,
      image: { type: "jpeg", quality: 0.7 },
      html2canvas: { scale: 1.7, useCORS: true, allowTaint: true },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    };

    html2pdf()
      .from(element)
      .set(options)
      .toPdf()
      .get("pdf")
      .then((pdf) => {
        pdf.save(fileName);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const PDFDetails = assessmentUserInfo ?? AssessmentResult?.info;

  if (from !== "viewAll" && (loader?.assessmentResult || !AssessmentResult)) {
    return <VSNLoader variant="fullPage" />;
  }

  return (
    <div className={classes.mainLayout}>
      {from !== "viewAll" ? (
        showBreadCrumbs ? (
          <Box mt={2.5} mb={1.8}>
            <VSNBreadcrumbs {...{ breadcrumbsRoutes }} />
          </Box>
        ) : (
          <Box className={classes.headingButton}>
            <Box>{breadCrumbAndButton}</Box>
            <Stack
              direction="row"
              gap={{ sm: 2, xs: 1 }}
              alignItems="center"
              flexWrap="wrap"
              sx={{ width: { xsm: "auto", xs: "100%" } }}
            >
              <VSNButton
                className={classes.PrintButton}
                handleClick={handleDownloadPDF}
                isLoading={loading}
              >
                <Stack direction="row" alignItems="center" gap={1}>
                  <PrintOutlinedIcon />
                  Print Medical Recommendations
                </Stack>
              </VSNButton>
              {AssessmentResult?.is_locked ? releaseButton : ""}
            </Stack>
          </Box>
        )
      ) : null}

      {AssessmentResult?.message === "No report found" ? (
        <Stack direction="row" justifyContent="center" p={5}>
          <VSNTypography variant="h5">No Report Found</VSNTypography>
        </Stack>
      ) : (
        <>
          {showBreadCrumbs && (
            <Stack
              direction={{ xsm: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <VSNTypography
                fontSize={{ xsm: 24, sm: 34 }}
                sx={{ fontWeight: 700 }}
              >
                Medical Recommendation Form
              </VSNTypography>
              <VSNButton
                className={classes.MRFPrintButton}
                handleClick={handleDownloadPDF}
                isLoading={loading}
              >
                <Stack direction="row" alignItems="center" gap={1}>
                  <PrintOutlinedIcon />
                  Print Medical Recommendations
                </Stack>
              </VSNButton>
            </Stack>
          )}

          <PrintComponent
            ref={printRef}
            result={AssessmentResult}
            header={PDFDetails}
          />
          <Box>
            <Grid item xl={6} lg={6} xs={12}>
              <Stack
                className={classes.assessmentReportCard}
                sx={{
                  background: "#FAF6F0 !important",
                }}
              >
                <VSNTypography
                  variant="h3"
                  component="h3"
                  className={classes.mdHeadingprimary}
                  mb={1}
                  sx={{
                    fontSize: "20px",
                    backgroundColor: "pink !importnant",
                    color: "#B29263 !important",
                    "&:before": { background: "#B29263 !important" },
                  }}
                >
                  {AssessmentResult?.mrf_card?.title ?? ""}
                </VSNTypography>
                <VSNTypography
                  component="p"
                  className={classes.desc}
                  fontSize={{ xsm: 16, sm: 16 }}
                >
                  {AssessmentResult?.mrf_card?.duty_with_limitation_and_days ??
                    ""}
                </VSNTypography>

                <Box py={1} display="flex">
                  <Box sx={{ height: 12, width: 12 }} mr={2} mt={0.5}>
                    <StarIcon />
                  </Box>
                  <VSNTypography
                    sx={{
                      fontSize: "18px",
                      fontWeight: 500,
                      color: "#0A0A0B",
                      lineHeight: "24px",
                    }}
                  >
                    {AssessmentResult?.mrf_card?.medical_condition ?? ""}
                  </VSNTypography>
                </Box>

                <VSNTypography component="p" className={classes.desc} pt={1}>
                  Provider Name :{" "}
                  {AssessmentResult?.mrf_card?.provider_name ?? "-"}
                </VSNTypography>
              </Stack>
            </Grid>

            <Stack
              className={classes.assessmentReportCard}
              position="relative"
              overflow="hidden"
              mt="25px"
              sx={{
                background: "#FAF6F0 !important",
              }}
            >
              <VSNTypography
                variant="h3"
                component="h3"
                className={classes.mdHeadingprimary}
                mb={1}
                sx={{
                  fontSize: "20px",
                  color: "#B29263 !important",
                  "&:before": { background: "#B29263 !important" },
                }}
              >
                {AssessmentResult?.performance_evaluation_card?.title ?? ""}
              </VSNTypography>
              <Stack
                direction={{ sm: "row" }}
                alignItems="center"
                spacing={4}
                mt={1.75}
              >
                <Stack
                  direction={{ md: "row" }}
                  sx={{
                    "&.MuiStack-root": {
                      position: "relative",
                      gap: "0 64px",
                      "&::after": {
                        content: { md: "''" },
                        position: "absolute",
                        top: 0,
                        left: "50% ",
                        height: "100%",
                        width: "1px",
                        backgroundColor: "#D5B98B",
                      },
                    },
                  }}
                  position="relative"
                >
                  <Stack
                    sx={{
                      "&.MuiStack-root": {
                        width: { xs: "100%", lg: "calc(50% - 32px)" },
                      },
                    }}
                  >
                    {AssessmentResult?.performance_evaluation_card?.tasks
                      ?.filter((_, index) => index % 2 === 0)
                      ?.map(({ is_checked, phrase }, index, arr) => {
                        const isLastElement = index === arr.length - 1;

                        return (
                          <Stack
                            direction="row"
                            alignItems="start"
                            gap={1}
                            key={index}
                            sx={{
                              borderBottom: isLastElement
                                ? "none"
                                : "1px solid #D5B98B",
                              height: "fit-content",
                            }}
                            padding={3}
                          >
                            <Checkbox
                              checked={is_checked}
                              sx={{
                                padding: 0,
                                paddingTop: "2.5px",
                                borderRadius: 4,
                                "&.Mui-checked": {
                                  color: "#B29263",
                                },
                                "&.MuiCheckbox-root": {
                                  color: "#B29263",
                                },
                              }}
                            />
                            <VSNTypography
                              fontSize={{ sm: "16px", smd: "20px" }}
                              sx={{ color: "var(--gray)", fontWeight: 400 }}
                            >
                              {phrase}
                            </VSNTypography>
                          </Stack>
                        );
                      })}
                  </Stack>
                  <Stack
                    sx={{
                      "&.MuiStack-root": {
                        width: { xs: "100%", lg: "calc(50% - 32px)" },
                      },
                    }}
                  >
                    {AssessmentResult?.performance_evaluation_card?.tasks
                      ?.filter((_, index) => index % 2 !== 0)
                      ?.map(({ is_checked, phrase }, index, arr) => {
                        const isLastElement = index === arr.length - 1;

                        return (
                          <Stack
                            direction="row"
                            alignItems="start"
                            gap={1}
                            key={index}
                            sx={{
                              borderBottom: isLastElement
                                ? "none"
                                : "1px solid #D5B98B",
                              height: "fit-content",
                            }}
                            padding={3}
                          >
                            <Checkbox
                              checked={is_checked}
                              sx={{
                                padding: 0,
                                paddingTop: "2.5px",
                                borderRadius: 4,
                                "&.Mui-checked": {
                                  color: "#B29263",
                                },
                                "&.MuiCheckbox-root": {
                                  color: "#B29263",
                                },
                              }}
                            />
                            <VSNTypography
                              fontSize={{ sm: "16px", smd: "20px" }}
                              sx={{ color: "var(--gray)", fontWeight: 400 }}
                            >
                              {phrase}
                            </VSNTypography>
                          </Stack>
                        );
                      })}
                  </Stack>
                  <Box position="absolute" sx={{ bottom: 0, right: 0 }}>
                    <VSNTypography
                      sx={{ textAlign: "end", paddingBottom: "6px" }}
                    >
                      Powered by
                    </VSNTypography>
                    <FrontLineLogo />
                  </Box>
                </Stack>
              </Stack>
            </Stack>
          </Box>
        </>
      )}
    </div>
  );
}
